import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
  Chip,
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getLocalChapterRosterDuesDetail,
  updateMemberValidation,
  updateLocalChapterRosterForm,
} from '../../../endpoints/rosterDues';
import { MemberInfo, RosterDuesDetail } from '../../../types/rosterDues';
import UserSearchModal from '../../../Components/UserSearchModal';
import AlertItem from '../../../Components/AlertItem';
import UserCreateModal from '../../../Components/UserCreateModal';
import MemberActionModal from '../../../Components/MemberActionModal';
import { local_chapter_roster_dues_statuses } from '../../../utils/variables';

const RosterDuesMemberList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('user_id');

  const [detail, setDetail] = useState<RosterDuesDetail | null>(null);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [localChapterId, setLocalChapterId] = useState<string>('');
  const [memberErrors, setMemberErrors] = useState<Record<string, string>>({});
  const updatingMemberIdRef = useRef<string | null>(null);
  const updatingStatusRef = useRef<'active' | 'inactive' | null>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState<string>('');
  const [editingAssigneeId, setEditingAssigneeId] = useState<string>('');
  const [editingAssigneeName, setEditingAssigneeName] = useState<string>('');
  const [editingApprovers, setEditingApprovers] = useState<
    Record<string, string>
  >({});
  const [editingApproverNames, setEditingApproverNames] = useState<
    Record<string, string>
  >({});
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [approverModalOpen, setApproverModalOpen] = useState(false);
  const [editingApproverStatusId, setEditingApproverStatusId] =
    useState<string>('');
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);

  // Add Member feature state
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [userCreateModalOpen, setUserCreateModalOpen] = useState(false);
  const [memberSelectModalOpen, setMemberSelectModalOpen] = useState(false);
  const [memberActionModalOpen, setMemberActionModalOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<
    'transfer' | 'reinstatement' | null
  >(null);

  // Self-removal confirmation dialog state
  const [selfRemovalConfirmOpen, setSelfRemovalConfirmOpen] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<
    'active' | 'inactive' | null
  >(null);

  const { data, isLoading, refetch } = useApiGet(
    ['local-chapter-roster-dues-detail', id],
    () => getLocalChapterRosterDuesDetail(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  useEffect(() => {
    // Clear state when id changes
    setDetail(null);
    setMembers([]);
    setLocalChapterId('');
    setMemberErrors({});
    setIsEditing(false);
    setEditingStatusId('');
    setEditingAssigneeId('');
    setEditingAssigneeName('');
    setEditingApprovers({});
    setEditingApproverNames({});
    setAlertState(null);
  }, [id]);

  // Refetch data when component mounts or when navigating back to ensure fresh data
  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id, refetch]);

  useEffect(() => {
    if (data) {
      setDetail(data);
      setMembers(data.members || []);
      if (data.local_chapter_id) {
        setLocalChapterId(data.local_chapter_id);
      }
      // Initialize editing state with current values (only if not currently editing)
      if (data.can_edit && !isEditing) {
        setEditingStatusId(data.status_id);
        setEditingAssigneeId(data.assigned_to_id);
        setEditingAssigneeName(data.assigned_to);
        // Initialize approvers map
        const approversMap: Record<string, string> = {};
        const approverNamesMap: Record<string, string> = {};
        data.approvers.forEach((approver) => {
          approversMap[approver.status_id] = approver.user_id;
          approverNamesMap[approver.status_id] = approver.user_name;
        });
        setEditingApprovers(approversMap);
        setEditingApproverNames(approverNamesMap);
      }
    }
  }, [data, isEditing]);

  const [isVerifying, setIsVerifying] = useState(false);

  const { mutate: updateMember, isPending: isUpdating } = useApiSend(
    (params: { formId: string; data: any }) => {
      return updateMemberValidation(params.formId, params.data);
    },
    () => {
      // On success, optimistically update the local state
      const memberId = updatingMemberIdRef.current;
      const newStatus = updatingStatusRef.current;

      if (memberId && newStatus) {
        // Check if the current user just marked themselves as inactive
        if (memberId === currentUserId && newStatus === 'inactive') {
          // User will lose access, redirect to roster dues list
          updatingMemberIdRef.current = null;
          updatingStatusRef.current = null;
          navigate('/annual-reports/roster-dues');
          return;
        }

        // Update local state
        setMembers((prevMembers) =>
          prevMembers.map((member) =>
            member.user_id === memberId
              ? { ...member, status: newStatus }
              : member
          )
        );

        // Clear any errors
        setMemberErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[memberId];
          return newErrors;
        });
        updatingMemberIdRef.current = null;
        updatingStatusRef.current = null;
      }
    },
    (error) => {
      console.error('Failed to update member status:', error);
      // Show error for the member that was being updated
      const memberId = updatingMemberIdRef.current;
      if (memberId) {
        setMemberErrors((prev) => ({
          ...prev,
          [memberId]: 'Failed to update member status. Please try again.',
        }));
        updatingMemberIdRef.current = null;
        updatingStatusRef.current = null;
      }
    }
  );

  // Sort and group members by status
  // Sort order: initiated, then reinstated, then transferred_in, then rest (maintaining server order)
  const sortMembers = (memberList: MemberInfo[]): MemberInfo[] => {
    return [...memberList].sort((a, b) => {
      // First priority: initiated
      if (a.initiated && !b.initiated) return -1;
      if (!a.initiated && b.initiated) return 1;

      // Second priority: reinstated (only if both have same initiated status)
      if (a.reinstated && !b.reinstated) return -1;
      if (!a.reinstated && b.reinstated) return 1;

      // Third priority: transferred_in (only if both have same initiated and reinstated status)
      if (a.transferred_in && !b.transferred_in) return -1;
      if (!a.transferred_in && b.transferred_in) return 1;

      // Otherwise maintain original order (server sorts by last_name, first_name)
      return 0;
    });
  };

  const nullMembers = sortMembers(members.filter((m) => m.status === null));
  const activeMembers = sortMembers(
    members.filter((m) => m.status === 'active')
  );
  const inactiveMembers = sortMembers(
    members.filter((m) => m.status === 'inactive')
  );

  const hasNullStatus = nullMembers.length > 0;
  // Only allow editing if detail is loaded and user is assigned
  const canEdit = detail !== null && detail.assigned_to_id === currentUserId;
  // Check if user has edit permission (different from canEdit which is for member status changes)
  const hasEditPermission = detail?.can_edit === true;

  const handleStatusChange = (
    userId: string,
    newStatus: 'active' | 'inactive'
  ) => {
    if (!detail || !id || !canEdit) return;

    // Check if user is marking themselves as inactive
    if (userId === currentUserId && newStatus === 'inactive') {
      // Show confirmation dialog
      setPendingUserId(userId);
      setPendingStatus(newStatus);
      setSelfRemovalConfirmOpen(true);
      return;
    }

    // Normal flow for other members
    proceedWithStatusChange(userId, newStatus);
  };

  // Extract the actual update logic into a separate function
  const proceedWithStatusChange = (
    userId: string,
    newStatus: 'active' | 'inactive'
  ) => {
    if (!detail || !id || !canEdit) return;

    // Track which member and status is being updated
    updatingMemberIdRef.current = userId;
    updatingStatusRef.current = newStatus;

    // Clear any previous error for this member
    setMemberErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[userId];
      return newErrors;
    });

    const updateData = {
      status: newStatus,
      user_id: userId,
      local_chapter_id: localChapterId || '', // Will be empty until backend includes it
    };

    updateMember({ formId: id, data: updateData });
  };

  // Handle confirmation dialog
  const handleConfirmSelfRemoval = () => {
    if (pendingUserId && pendingStatus) {
      setSelfRemovalConfirmOpen(false);
      proceedWithStatusChange(pendingUserId, pendingStatus);
      setPendingUserId(null);
      setPendingStatus(null);
    }
  };

  const handleCancelSelfRemoval = () => {
    setSelfRemovalConfirmOpen(false);
    setPendingUserId(null);
    setPendingStatus(null);
  };

  const handleContinueToSummary = async () => {
    if (!id || hasNullStatus) return;

    // Verify by calling the details endpoint before allowing navigation
    setIsVerifying(true);
    try {
      const verificationData = await getLocalChapterRosterDuesDetail(id);
      if (verificationData) {
        const nullCount =
          verificationData.members?.filter((m) => m.status === null).length ||
          0;

        if (nullCount === 0) {
          // All members have statuses, allow navigation
          navigate(`/annual-reports/roster-dues/${id}/summary`);
        } else {
          // Still have null statuses, update the local state and show warning
          setMembers(verificationData.members || []);
          setMemberErrors({
            _general:
              'Some members still need status assignment to them. Please assign a status (Dues Received or No Dues Received) to each member before continuing to the summary page.',
          });
        }
      }
    } catch (error) {
      console.error('Failed to verify member statuses:', error);
      setMemberErrors({
        _general: 'Failed to verify member statuses. Please try again.',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Edit mode handlers
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    // Reset to original values
    if (detail) {
      setEditingStatusId(detail.status_id);
      setEditingAssigneeId(detail.assigned_to_id);
      setEditingAssigneeName(detail.assigned_to);
      const approversMap: Record<string, string> = {};
      const approverNamesMap: Record<string, string> = {};
      detail.approvers.forEach((approver) => {
        approversMap[approver.status_id] = approver.user_id;
        approverNamesMap[approver.status_id] = approver.user_name;
      });
      setEditingApprovers(approversMap);
      setEditingApproverNames(approverNamesMap);
    }
    setIsEditing(false);
    setAlertState(null);
  };

  const handleAssigneeSelect = (user: any) => {
    // UserSearchModal with userObjectRet=true returns the full user object
    const userId = user.id;
    const userName = user.full_name || 'Unknown';
    setEditingAssigneeId(userId);
    setEditingAssigneeName(userName);
    setAssigneeModalOpen(false);
  };

  const handleApproverSelect = (user: any) => {
    // UserSearchModal with userObjectRet=true returns the full user object
    if (editingApproverStatusId) {
      const userId = user.id;
      const userName = user.full_name || 'Unknown';
      setEditingApprovers((prev) => ({
        ...prev,
        [editingApproverStatusId]: userId,
      }));
      setEditingApproverNames((prev) => ({
        ...prev,
        [editingApproverStatusId]: userName,
      }));
    }
    setApproverModalOpen(false);
    setEditingApproverStatusId('');
  };

  const handleOpenApproverModal = (statusId: string) => {
    setEditingApproverStatusId(statusId);
    setApproverModalOpen(true);
  };

  const { mutate: updateForm, isPending: isUpdatingForm } = useApiSend(
    (params: { formId: string; data: any }) => {
      return updateLocalChapterRosterForm(params.formId, params.data);
    },
    () => {
      // On success: show alert, wait 2 seconds, then refetch
      setAlertState({
        severity: 'success',
        description: 'Form updated successfully!',
        title: 'Success',
      });
      setIsEditing(false);
      setTimeout(() => {
        refetch();
        setAlertState(null);
      }, 2000);
    },
    (error) => {
      console.error('Failed to update form:', error);
      setAlertState({
        severity: 'error',
        description: 'Failed to update form. Please try again.',
        title: 'Error',
      });
    },
    [['local-chapter-roster-dues-detail', id], ['roster-dues-list']]
  );

  const handleSaveEdit = () => {
    if (!id || !detail) return;

    // Build update request with only changed fields
    const updateData: any = {};

    if (editingStatusId !== detail.status_id) {
      updateData.status_id = editingStatusId;
    }

    if (editingAssigneeId !== detail.assigned_to_id) {
      updateData.assignee_id = editingAssigneeId;
    }

    // Check for approver changes
    const approverUpdates: Array<{
      status_id: string;
      approver_id: string | null;
    }> = [];
    const eligibleStatuses = local_chapter_roster_dues_statuses.filter(
      (s) => s.id !== '1d6ebbdb98334816b6ace293463d9069' // Exclude "Local Chapter Completed"
    );

    eligibleStatuses.forEach((status) => {
      const currentApprover = detail.approvers.find(
        (a) => a.status_id === status.id
      );
      const currentApproverId = currentApprover?.user_id || null;
      const newApproverId = editingApprovers[status.id] || null;

      if (currentApproverId !== newApproverId) {
        approverUpdates.push({
          status_id: status.id,
          approver_id: newApproverId,
        });
      }
    });

    if (approverUpdates.length > 0) {
      updateData.approvers = approverUpdates;
    }

    // Only send request if there are changes
    if (Object.keys(updateData).length > 0) {
      updateForm({ formId: id, data: updateData });
    } else {
      // No changes, just exit edit mode
      setIsEditing(false);
    }
  };

  // Get eligible statuses for editing (excluding "Local Chapter Completed")
  const eligibleStatuses = local_chapter_roster_dues_statuses
    .filter((s) => s.id !== '1d6ebbdb98334816b6ace293463d9069')
    .sort((a, b) => a.order - b.order);

  // Get approver name for a status
  const getApproverName = (statusId: string): string => {
    if (isEditing) {
      const approverId = editingApprovers[statusId];
      if (!approverId) return 'Not assigned';
      // Use stored name if available, otherwise try to find in current approvers
      if (editingApproverNames[statusId]) {
        return editingApproverNames[statusId];
      }
      const approver = detail?.approvers.find((a) => a.user_id === approverId);
      return approver?.user_name || 'Not assigned';
    } else {
      const approver = detail?.approvers.find((a) => a.status_id === statusId);
      return approver?.user_name || 'Not assigned';
    }
  };

  // Get assignee name
  const getAssigneeName = (): string => {
    if (isEditing) {
      if (editingAssigneeName) {
        return editingAssigneeName;
      }
      return detail?.assigned_to || 'Not assigned';
    }
    return detail?.assigned_to || 'Not assigned';
  };

  // Add Member feature handlers
  const handleAddMemberClick = () => {
    setAddMemberDialogOpen(true);
  };

  const handleInitiateNewMember = () => {
    setAddMemberDialogOpen(false);
    setUserCreateModalOpen(true);
  };

  const handleTransferMember = () => {
    setAddMemberDialogOpen(false);
    setActionType('transfer');
    setMemberSelectModalOpen(true);
  };

  const handleReinstateMember = () => {
    setAddMemberDialogOpen(false);
    setActionType('reinstatement');
    setMemberSelectModalOpen(true);
  };

  const handleMemberSelected = (user: any) => {
    setMemberSelectModalOpen(false);
    setSelectedMemberId(user.id);
    setMemberActionModalOpen(true);
  };

  const handleMemberActionSuccess = () => {
    setMemberActionModalOpen(false);
    setSelectedMemberId(null);
    setActionType(null);
    refetch(); // Refresh the member list
  };

  const handleUserCreateSuccess = (userId: string) => {
    setUserCreateModalOpen(false);
    refetch(); // Refresh the member list
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      {alertState && (
        <Grid item xs={12} padding={2}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}

      <Grid item xs={12} padding={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" color={'primary'}>
            {detail?.local_chapter_name || 'Roster & Dues Form'}
          </Typography>
          {hasEditPermission && !isEditing && (
            <IconButton
              onClick={handleEditClick}
              color="primary"
              size="small"
              sx={{ ml: 1 }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        {isEditing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={editingStatusId}
                label="Status"
                onChange={(e) => setEditingStatusId(e.target.value)}
              >
                {eligibleStatuses.map((status) => (
                  <MenuItem key={status.id} value={status.id}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setAssigneeModalOpen(true)}
            >
              Assignee: {getAssigneeName()}
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Status:</strong> {detail?.status_name || 'Loading...'}
            </Typography>
            {detail?.assigned_to && (
              <Typography variant="body1">
                <strong>Assigned to:</strong> {detail.assigned_to}
              </Typography>
            )}
          </Box>
        )}
      </Grid>

      {/* Approvers Section - Only show when hasEditPermission is true */}
      {hasEditPermission && (
        <Grid item xs={12} padding={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Approvers
            </Typography>
            {eligibleStatuses.map((status) => (
              <Box key={status.id} sx={{ mb: 1.5 }}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 200 }}>
                      {status.name}:
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenApproverModal(status.id)}
                    >
                      {getApproverName(status.id)}
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2">
                    {status.name}: {getApproverName(status.id)}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        </Grid>
      )}

      {/* Edit Mode Action Buttons */}
      {isEditing && (
        <Grid item xs={12} padding={2}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              disabled={isUpdatingForm}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={isUpdatingForm}
              startIcon={isUpdatingForm ? <CircularProgress size={16} /> : null}
            >
              {isUpdatingForm ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Grid>
      )}

      {!isLoading && detail && !canEdit && (
        <Grid item xs={12} padding={2}>
          <Alert severity="info">
            You are viewing this form in read-only mode. Only the assigned user
            can make changes.
          </Alert>
        </Grid>
      )}

      {hasNullStatus && (
        <>
          <Grid item xs={12} padding={2}>
            <Alert
              severity="error"
              sx={{
                '& .MuiAlert-message': { color: 'error.dark' },
              }}
            >
              If a deceased member is listed below, contact your corresponding
              secretary to have her report date of death before proceeding.
            </Alert>
          </Grid>
          <Grid item xs={12} padding={2}>
            <Alert severity="warning">
              Some members do not have a status assigned to them. Please assign
              a status (Dues Received or No Dues Received) to each member before
              continuing to the summary page.
            </Alert>
          </Grid>
        </>
      )}

      {memberErrors._general && (
        <Grid item xs={12} padding={2}>
          <AlertItem
            severity="error"
            description={memberErrors._general}
            onClose={() => {}}
            keepOpen={true}
          />
        </Grid>
      )}

      {isLoading ? (
        <Grid item xs={12} textAlign="center" padding={4}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {/* Null Status Members */}
          {nullMembers.length > 0 && (
            <>
              <Grid item xs={12} paddingTop={2}>
                <Typography variant="h6" color="error">
                  Members Needing Status Assignment ({nullMembers.length})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  {nullMembers.map((member) => (
                    <Box key={member.user_id}>
                      <Grid
                        container
                        spacing={2}
                        sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}
                        alignItems="center"
                      >
                        <Grid item xs={12} md={6}>
                          <Typography variant="body1" fontWeight="medium">
                            {member.name}
                          </Typography>
                          <Box
                            sx={{
                              mt: 1,
                              display: 'flex',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            {member.initiated && (
                              <Chip
                                label="Initiated"
                                size="small"
                                color="primary"
                              />
                            )}
                            {member.reinstated && (
                              <Chip
                                label="Reinstated"
                                size="small"
                                color="secondary"
                              />
                            )}
                            {member.transferred_in && (
                              <Chip
                                label="Transferred In"
                                size="small"
                                color="info"
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={!canEdit || isUpdating}
                              onClick={() =>
                                handleStatusChange(member.user_id, 'active')
                              }
                            >
                              Dues Received
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              disabled={!canEdit || isUpdating}
                              onClick={() =>
                                handleStatusChange(member.user_id, 'inactive')
                              }
                            >
                              No Dues Received
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                      {memberErrors[member.user_id] && (
                        <Box sx={{ mb: 2 }}>
                          <AlertItem
                            severity="error"
                            description={memberErrors[member.user_id]}
                            onClose={() => {}}
                            keepOpen={true}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} paddingTop={2}>
                <Divider />
              </Grid>
            </>
          )}

          {/* Active Members */}
          {activeMembers.length > 0 && (
            <>
              <Grid item xs={12} paddingTop={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" color="success.main">
                    Dues Received Members ({activeMembers.length})
                  </Typography>
                  {canEdit && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddMemberClick}
                    >
                      Add Member
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  {activeMembers.map((member) => (
                    <Box key={member.user_id}>
                      <Grid
                        container
                        spacing={2}
                        sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}
                        alignItems="center"
                      >
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Typography variant="body1" fontWeight="medium">
                              {member.name}
                            </Typography>
                            <Chip
                              label="Dues Received"
                              size="small"
                              color="success"
                            />
                          </Box>
                          <Box
                            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                          >
                            {member.initiated && (
                              <Chip
                                label="Initiated"
                                size="small"
                                color="primary"
                              />
                            )}
                            {member.reinstated && (
                              <Chip
                                label="Reinstated"
                                size="small"
                                color="secondary"
                              />
                            )}
                            {member.transferred_in && (
                              <Chip
                                label="Transferred In"
                                size="small"
                                color="info"
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={!canEdit || isUpdating}
                            onClick={() =>
                              handleStatusChange(member.user_id, 'inactive')
                            }
                          >
                            Mark as No Dues Received
                          </Button>
                        </Grid>
                      </Grid>
                      {memberErrors[member.user_id] && (
                        <Box sx={{ mb: 2 }}>
                          <AlertItem
                            severity="error"
                            description={memberErrors[member.user_id]}
                            onClose={() => {}}
                            keepOpen={true}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} paddingTop={2}>
                <Divider />
              </Grid>
            </>
          )}

          {/* Inactive Members */}
          {inactiveMembers.length > 0 && (
            <>
              <Grid item xs={12} paddingTop={2}>
                <Typography variant="h6" color="text.secondary">
                  No Dues Received Members ({inactiveMembers.length})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  {inactiveMembers.map((member) => (
                    <Box key={member.user_id}>
                      <Grid
                        container
                        spacing={2}
                        sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}
                        alignItems="center"
                      >
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Typography variant="body1" fontWeight="medium">
                              {member.name}
                            </Typography>
                            <Chip
                              label="No Dues Received"
                              size="small"
                              color="default"
                            />
                          </Box>
                          <Box
                            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                          >
                            {member.initiated && (
                              <Chip
                                label="Initiated"
                                size="small"
                                color="primary"
                              />
                            )}
                            {member.reinstated && (
                              <Chip
                                label="Reinstated"
                                size="small"
                                color="secondary"
                              />
                            )}
                            {member.transferred_in && (
                              <Chip
                                label="Transferred In"
                                size="small"
                                color="info"
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            disabled={!canEdit || isUpdating}
                            onClick={() =>
                              handleStatusChange(member.user_id, 'active')
                            }
                          >
                            Mark as Dues Received
                          </Button>
                        </Grid>
                      </Grid>
                      {memberErrors[member.user_id] && (
                        <Box sx={{ mb: 2 }}>
                          <AlertItem
                            severity="error"
                            description={memberErrors[member.user_id]}
                            onClose={() => {}}
                            keepOpen={true}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </>
          )}

          {members.length === 0 && (
            <Grid item xs={12} padding={4} textAlign="center">
              <Typography variant="body1">No members found.</Typography>
            </Grid>
          )}
        </>
      )}

      <Grid item xs={12} paddingTop={4} paddingBottom={2}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/annual-reports/roster-dues')}
            disabled={isVerifying || isEditing}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            onClick={handleContinueToSummary}
            disabled={hasNullStatus || isVerifying || isUpdating || isEditing}
          >
            {isVerifying ? 'Verifying...' : 'Continue to Dues Form'}
          </Button>
        </Box>
      </Grid>

      {/* User Search Modals */}
      <UserSearchModal
        modalOpen={assigneeModalOpen}
        setModalOpen={setAssigneeModalOpen}
        setUser={handleAssigneeSelect}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
        allowSetToMe={true}
      />
      <UserSearchModal
        modalOpen={approverModalOpen}
        setModalOpen={setApproverModalOpen}
        setUser={handleApproverSelect}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
        allowSetToMe={true}
      />

      {/* Add Member Dialog */}
      <Dialog
        open={addMemberDialogOpen}
        onClose={() => setAddMemberDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Member</DialogTitle>
        <DialogContent>
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleInitiateNewMember}>
                <ListItemText primary="Initiate New Member" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleTransferMember}>
                <ListItemText primary="Transfer a Member" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleReinstateMember}>
                <ListItemText primary="Reinstate Member" />
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* User Create Modal */}
      <UserCreateModal
        open={userCreateModalOpen}
        onClose={() => setUserCreateModalOpen(false)}
        onSuccess={handleUserCreateSuccess}
        successRedirectPath={
          id ? `/annual-reports/roster-dues/${id}/members` : undefined
        }
      />

      {/* Member Select Modal (for Transfer/Reinstate) */}
      <UserSearchModal
        modalOpen={memberSelectModalOpen}
        setModalOpen={setMemberSelectModalOpen}
        setUser={handleMemberSelected}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
        allowSetToMe={false}
        statusFilter="all"
      />

      {/* Member Action Modal */}
      {selectedMemberId && actionType && (
        <MemberActionModal
          open={memberActionModalOpen}
          onClose={() => {
            setMemberActionModalOpen(false);
            setSelectedMemberId(null);
            setActionType(null);
          }}
          userId={selectedMemberId}
          actionType={actionType}
          onSuccess={handleMemberActionSuccess}
        />
      )}

      {/* Self-Removal Confirmation Dialog */}
      <Dialog
        open={selfRemovalConfirmOpen}
        onClose={handleCancelSelfRemoval}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            You are about to mark yourself as "No Dues Received". This action
            will remove your roles and membership, which will also remove your
            ability to view or edit this form.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSelfRemoval} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSelfRemoval}
            variant="contained"
            color="error"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : null}
          >
            {isUpdating ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default RosterDuesMemberList;
